/**
 * Pure-function builders for the `sync.quizzes.share()` /
 * `sync.quizzes.unshare()` extensions. The actual `sync` schema wraps
 * these with `ops.set()` calls; keeping the logic pure here makes it
 * testable without standing up the sync engine.
 *
 * Both builders return a "next entry" plus minimal metadata, leaving
 * the caller to decide whether to commit (e.g., `ops.set(next)`) or
 * short-circuit (when `next === null`, nothing changed).
 */

import type { SyncQuizProgressEntry } from '../../sync-engine/schema/shared-types';
import { mintShareCode as defaultMintShareCode } from './code';

export interface BuildShareUpdateResult {
  /** Next entry to write, or `null` to indicate no write is needed
   *  (entry missing, tombstoned, or already shared idempotently). */
  next: SyncQuizProgressEntry | null;
  /** The shareCode that's now active on the entry, or `null` when the
   *  entry can't be shared (missing / tombstoned). For idempotent
   *  re-share this returns the existing code unchanged. */
  code: string | null;
}

export interface BuildShareUpdateOptions {
  /** Override the timestamp used when stamping `sharedAt` on a
   *  newly-shared entry. Defaults to `new Date().toISOString()`. */
  now?: string;
  /** Override the share-code mint function. Defaults to the cryptographic
   *  `mintShareCode()`. Test code can inject a deterministic generator. */
  mintCode?: () => string;
}

/**
 * Compute the next entry shape for `sync.quizzes.share(...)`.
 *
 * Cases:
 *   - missing entry         → `{ next: null, code: null }`
 *   - tombstoned entry      → `{ next: null, code: null }`
 *   - already shared (both
 *     sharedAt + shareCode) → `{ next: null, code: existingCode }`  (idempotent)
 *   - fresh share           → `{ next: stampedEntry, code: newCode }`
 *
 * Re-share semantics: when only one of (sharedAt, shareCode) is set —
 * which shouldn't happen in normal flows but defends against partial
 * writes — the missing side is filled in and the existing one is kept.
 */
export function buildShareUpdate(
  entry: SyncQuizProgressEntry | undefined,
  opts: BuildShareUpdateOptions = {},
): BuildShareUpdateResult {
  if (!entry || entry.deletedTimeISO) return { next: null, code: null };
  if (entry.sharedAt && entry.shareCode) {
    // Idempotent: no mutation, return the existing code.
    return { next: null, code: entry.shareCode };
  }
  const mintCode = opts.mintCode ?? defaultMintShareCode;
  const now = opts.now ?? new Date().toISOString();
  const code = entry.shareCode ?? mintCode();
  const next: SyncQuizProgressEntry = {
    ...entry,
    sharedAt: entry.sharedAt ?? now,
    shareCode: code,
  };
  return { next, code };
}

/**
 * Compute the next entry shape for `sync.quizzes.unshare(...)`. Returns
 * `null` when there's nothing to clear (missing entry or already
 * unshared).
 */
export function buildUnshareUpdate(
  entry: SyncQuizProgressEntry | undefined,
): SyncQuizProgressEntry | null {
  if (!entry) return null;
  if (!entry.sharedAt && !entry.shareCode) return null;
  const next: SyncQuizProgressEntry = { ...entry };
  delete next.sharedAt;
  delete next.shareCode;
  return next;
}
